import * as React from 'react';

import { WithLocalizedContent } from '@coursera-web/internationalization-lib';
import type { Locale$1 as Locale } from '@coursera-web/shared-articles';
import type { CountryCode, LanguageCode } from '__generated__/graphql-types';

import type { ArticleFieldsFragment } from 'bundles/unified-seo-content-common/__generated__/contentfulTypes';
import PageDataErrorHandler from 'bundles/unified-seo-content-common/components/shared/PageDataErrorHandler';
import { SEO_CONTENT_PAGE_TYPE } from 'bundles/unified-seo-content-common/constants';
import useArticleLocalesData from 'bundles/unified-seo-content-common/hooks/useArticleLocalesData';
import type { LocalesDataProps } from 'bundles/unified-seo-content-common/hooks/useArticleLocalesData';
import useSeoContentHubPageLink from 'bundles/unified-seo-content-common/hooks/useSeoContentHubPageLink';
import { ArticleFieldsFragment as ArticleFieldsFragmentDoc } from 'bundles/unified-seo-content-common/queries/ArticleQuery';

type ArticleContentPageContentfulData = {
  pageDataLoading?: boolean;
  subfolderLocaleCode?: Locale;
  pageData?: ArticleFieldsFragment | null;
  slug: string;
  hubPageLink: string;
  isPreview?: boolean;
};

export type ArticleContentPageData = ArticleContentPageContentfulData & LocalesDataProps;

type PropsForQuery = {
  slug: string;
  isPreview: boolean;
  countryCode: CountryCode;
  languageCode: LanguageCode;
};

type Props = {
  slug: string;
  isPreview: boolean;
  subfolderLocaleCode?: Locale;
  children: (data: ArticleContentPageData) => JSX.Element | null;
};

const ARTICLE_CONTENT_TYPE = 'article';
const ARTICLE_FRAGMENT_FIELDS_NAME = 'ArticleFields';

const getWithLocalizedContentProps = ({ isPreview, countryCode, languageCode, slug }: PropsForQuery) => ({
  isPreview,
  contentType: ARTICLE_CONTENT_TYPE,
  fragmentDoc: ArticleFieldsFragmentDoc,
  fragmentFieldsName: ARTICLE_FRAGMENT_FIELDS_NAME,
  countryCode,
  languageCode,
  filters: { slug, countryCode },
  fallbackComponent: () => <PageDataErrorHandler />,
});

export const ArticleContentPageDataProvider = ({ children, slug, subfolderLocaleCode, isPreview }: Props) => {
  const hubPageLink = useSeoContentHubPageLink({
    pageType: SEO_CONTENT_PAGE_TYPE.articles,
    preview: isPreview,
    subfolderLocaleCode,
  });
  const { locales, defaultRegionCountryCode } = useArticleLocalesData({ slug, preview: isPreview });
  const isLocalizedArticle = !!subfolderLocaleCode;
  const countryCode = isLocalizedArticle ? subfolderLocaleCode.countryCode : 'global';
  const languageCode = isLocalizedArticle ? subfolderLocaleCode.languageCode : 'global';
  return (
    <WithLocalizedContent<ArticleFieldsFragment>
      {...getWithLocalizedContentProps({
        isPreview,
        slug,
        countryCode: countryCode as CountryCode,
        languageCode,
      })}
    >
      {({ loading, data }) =>
        children({
          pageData: data,
          pageDataLoading: loading,
          subfolderLocaleCode,
          locales,
          slug,
          hubPageLink,
          isPreview,
          defaultRegionCountryCode,
        })
      }
    </WithLocalizedContent>
  );
};

export default ArticleContentPageDataProvider;
